import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { ACCENT } from "../artistStudio/shared";
import { isSignedIn, apiMessage, storedUserId } from "./fanHomeApi";
import { discoveryApi } from "./discoveryApi";
import { soundApi, formatDate, money } from "../soundDetail/soundApi";
import { receiptLink } from "../receipts/receiptApi";
import { initOffline, isOfflineSupported, listOffline, removeOffline, subscribeOffline } from "../soundDetail/offlineStore";
import AutoRenewSwitch from "./AutoRenewSwitch";
import RenewalCardDialog from "./RenewalCardDialog";
import { toast } from "react-toastify";
import MusicShell from "./MusicShell";
import { LoadMore, GridSkeleton, Notice } from "./discoveryParts";
import { useErrorToast } from "../common/errorToast";
import { CoverArt, INK, MUTED, ListPanel, ListSkeleton, SoundRow, PlaylistNameDialog, SolidButton, useToast, rowLine } from "./libraryParts";

// My Library, laid out like the My Library screens in the Claude design: the sounds I saved, my playlists, My Licences
// (what I may use, and until when) and Downloads (sounds kept for offline listening on this browser). The open tab lives in
// the address (?tab=playlists, ?tab=licences, ?tab=downloads) so refresh and Back come back to the same tab.
const TABS = [["saved", "Saved Sounds"], ["playlists", "Playlists"], ["licences", "My Licences"], ["downloads", "Downloads"]];
const PAGE_SIZE = 20;
const SOON_DAYS = 30; // a licence ending within this many days is marked and offers Renew

// Loads a paged list one page at a time and keeps what was already loaded on screen. `removeWhere` takes an item out at once.
function usePagedList(fetcher) {
  const [state, setState] = useState({ status: "loading", items: [], page: 1, hasMore: false, total: 0, error: "", busy: false });
  const ticket = useRef(0);
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;
  const load = useCallback(async (page = 1) => {
    const mine = ++ticket.current;
    setState((s) => (page === 1 ? { ...s, status: "loading", error: "" } : { ...s, busy: true, error: "" }));
    try {
      const data = await fetchRef.current({ page, limit: PAGE_SIZE });
      if (mine !== ticket.current) return;
      setState((s) => ({
        status: "ready", items: page === 1 ? data.items || [] : [...s.items, ...(data.items || [])], page,
        hasMore: Boolean(data.has_more), total: Number(data.total) || 0, error: "", busy: false
      }));
    } catch (e) {
      if (mine === ticket.current) setState((s) => ({ ...s, status: s.items.length && page > 1 ? "ready" : "error", busy: false, error: apiMessage(e, "We couldn't load this.") }));
    }
  }, []);
  useEffect(() => { load(1); }, [load]);
  const removeWhere = (test) => setState((s) => ({ ...s, items: s.items.filter((i) => !test(i)), total: Math.max(0, s.total - 1) }));
  const restore = (item, index) => setState((s) => ({ ...s, items: [...s.items.slice(0, index), item, ...s.items.slice(index)], total: s.total + 1 }));
  const prepend = (item) => setState((s) => ({ ...s, items: [item, ...s.items], total: s.total + 1 }));
  return { ...state, reload: () => load(1), more: () => load(state.page + 1), removeWhere, restore, prepend };
}

function Body({ list, empty, skeleton = <ListSkeleton />, children }) {
  useErrorToast(list.status === "error" ? "" : list.error); // a failed "Load more" is a popup; a failed first load keeps the Retry panel
  if (list.status === "loading" && !list.items.length) return skeleton;
  if (list.status === "error") {
    return (
      <Notice action={<SolidButton onClick={list.reload} className="mt-3">Retry</SolidButton>}>
        <span role="alert">{list.error}</span>
      </Notice>
    );
  }
  if (!list.items.length) return <Notice>{empty}</Notice>;
  return (
    <>
      {children}
      {list.hasMore && <LoadMore onClick={list.more} busy={list.busy} />}
    </>
  );
}

const findSounds = <Link to="/fan/search" className="font-semibold underline" style={{ color: ACCENT }}>Find sounds</Link>;
const countOf = (n) => `${n} ${n === 1 ? "sound" : "sounds"}`;

// ---- tab 1: saved sounds ---------------------------------------------------------------------------------------------------
function Saved({ onTotal, notify }) {
  const list = usePagedList(soundApi.saved);
  const [busyId, setBusyId] = useState(null);
  useEffect(() => { if (list.status === "ready") onTotal(list.total); }, [list.status, list.total]); // eslint-disable-line react-hooks/exhaustive-deps

  // The row goes at once. If the server says no, it comes back where it was and the reason is shown.
  const unsave = async (track) => {
    if (busyId === track.id) return;
    const index = list.items.findIndex((i) => i.id === track.id);
    setBusyId(track.id);
    list.removeWhere((i) => i.id === track.id);
    try {
      await soundApi.unsave(track.id);
      notify(`“${track.title}” was removed from your saved sounds.`);
    } catch (e) {
      list.restore(track, Math.max(0, index));
      notify(apiMessage(e, "We couldn't remove this sound. Please try again."));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Body list={list} empty={<>You haven't saved any sounds yet. Tap Save on a sound page and it will show up here. {findSounds}</>}>
      <ListPanel>
        <ul data-testid="saved-list">
          {list.items.map((t, i, all) => (
            <SoundRow
              key={t.id} track={t} queue={all}
              action={<button type="button" onClick={() => unsave(t)} disabled={busyId === t.id} className="shrink-0 text-[13px] font-semibold hover:text-red-700 disabled:opacity-60" style={{ color: MUTED }} aria-label={`Unsave ${t.title}`}>Unsave</button>}
            />
          ))}
        </ul>
      </ListPanel>
    </Body>
  );
}

// ---- tab 2: playlists ------------------------------------------------------------------------------------------------------
function PlaylistTile({ playlist }) {
  const to = `/fan/playlists/${playlist.id}`;
  return (
    <article className="min-w-0" aria-label={`Playlist ${playlist.name}`}>
      <Link to={to} className="block aspect-square overflow-hidden rounded-xl" tabIndex={-1} aria-hidden="true"><CoverArt url={playlist.cover_url} seed={playlist.id} className="h-full w-full" /></Link>
      <Link to={to} className="mt-2.5 block truncate text-[13px] font-semibold hover:underline" style={{ color: INK }} title={playlist.name}>{playlist.name}</Link>
      <p className="text-xs" style={{ color: MUTED }}>{countOf(Number(playlist.track_count) || 0)}</p>
    </article>
  );
}
const PLAYLIST_GRID = "grid grid-cols-2 gap-4 md:grid-cols-3";

function Playlists({ onTotal, notify }) {
  const list = usePagedList(discoveryApi.playlists);
  const [creating, setCreating] = useState(false);
  useEffect(() => { if (list.status === "ready") onTotal(list.total); }, [list.status, list.total]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (name) => {
    const made = await discoveryApi.createPlaylist(name);
    list.prepend({ track_count: 0, cover_url: null, ...made });
    setCreating(false);
    notify(`Playlist “${made.name}” created.`);
  };

  return (
    <>
      <div className="mb-5 flex justify-end">
        <SolidButton onClick={() => setCreating(true)} data-testid="create-playlist">+ New Playlist</SolidButton>
      </div>
      <Body list={list} skeleton={<GridSkeleton />} empty="You don't have any playlists yet. Use “Add to playlist” on a sound page to make one, or press + New Playlist.">
        <div className={PLAYLIST_GRID}>{list.items.map((p) => <PlaylistTile key={p.id} playlist={p} />)}</div>
      </Body>
      {creating && <PlaylistNameDialog title="New playlist" confirmLabel="Create Playlist" taken={list.items.map((p) => p.name)} onSubmit={create} onClose={() => setCreating(false)} />}
    </>
  );
}

// ---- tab 3: my licences ----------------------------------------------------------------------------------------------------
const TERM = { one_year: "1 year", lifetime: "Lifetime" };
const COLUMNS = "md:grid-cols-[minmax(0,2.2fr)_minmax(0,1.3fr)_minmax(0,1.3fr)_minmax(0,1.2fr)_minmax(0,1.5fr)_minmax(0,1fr)]";
const HEADINGS = ["Track", "Usage class", "Licence ID", "Obtained", "Validity", "Certificate"];
const AMBER = "#b45309";

// ending soon = still active, but the end date is within SOON_DAYS
const endsSoon = (l) => {
  if (l.status === "expired" || !l.expires_at) return false;
  const left = new Date(l.expires_at).getTime() - Date.now();
  return left > 0 && left <= SOON_DAYS * 86400000;
};

// A renewal that failed: what happened, when it is tried again, and a way to fix the card.
function RenewalProblem({ renewal, onUpdateCard }) {
  const stopped = renewal.state === "stopped";
  return (
    <div className="mt-1 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs" style={{ color: AMBER }} role="status" data-testid="renewal-problem">
      <p className="font-semibold">
        {stopped ? "Automatic renewal is off after the failed payment." : `Renewal payment failed (attempt ${renewal.failed_attempts} of ${renewal.attempts_total}).`}
      </p>
      {renewal.reason && <p>{renewal.reason}</p>}
      {!stopped && renewal.next_attempt_at && <p>We will try again on {formatDate(renewal.next_attempt_at)}.</p>}
      {renewal.can_update_card && <button type="button" onClick={onUpdateCard} className="mt-1 font-semibold underline" data-testid="update-card">Update card</button>}
    </div>
  );
}

function Licences({ onTotal }) {
  const list = usePagedList(discoveryApi.licences);
  const [updatingCard, setUpdatingCard] = useState(false);
  useEffect(() => { if (list.status === "ready") onTotal(list.total); }, [list.status, list.total]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Body list={list} empty={<>No licences yet. Pick a licence on a sound page and it will be listed here with its ID. {findSounds}</>}>
      <ListPanel>
        <div className={`hidden gap-4 px-[18px] py-3 text-[11px] font-semibold uppercase tracking-wider md:grid ${COLUMNS}`} style={{ color: ACCENT }} aria-hidden="true">
          {HEADINGS.map((h, i) => <span key={h} className={i === HEADINGS.length - 1 ? "text-right" : ""}>{h}</span>)}
        </div>
        <ul data-testid="licence-list">
          {list.items.map((l) => {
            const expired = l.status === "expired";
            const soon = endsSoon(l);
            const validity = l.expires_at ? (expired ? `Expired ${formatDate(l.expires_at)}` : `Until ${formatDate(l.expires_at)}`) : "No expiry";
            const price = Number(l.price_paid) === 0 ? "Free" : money(l.price_paid, l.currency);
            return (
              <li key={l.id} className={`grid gap-x-4 gap-y-1 border-t px-[18px] py-3 text-[13px] first:border-t-0 md:items-center ${COLUMNS} ${expired ? "opacity-85" : ""}`} style={rowLine}>
                <div className="min-w-0">
                  <p className="truncate font-semibold" style={{ color: expired ? MUTED : INK }}>{l.title}</p>
                  <p className="truncate text-xs" style={{ color: MUTED }}>{l.artist_name || "Unknown artist"}</p>
                </div>
                <div className="min-w-0">
                  <p className="font-medium" style={{ color: INK }}><span className="md:hidden" style={{ color: MUTED }}>Usage class: </span>{l.usage_label}</p>
                  <p className="text-xs" style={{ color: MUTED }}>{TERM[l.term] || l.term} · {price}</p>
                  {l.can_auto_renew && <AutoRenewSwitch key={`${l.id}-${l.auto_renew}`} licence={l} />}
                  {l.renewal && <RenewalProblem renewal={l.renewal} onUpdateCard={() => setUpdatingCard(true)} />}
                </div>
                <p className="min-w-0 truncate" style={{ color: MUTED }}><span className="md:hidden">Licence ID: </span>{l.reference}</p>
                <p style={{ color: MUTED }}><span className="md:hidden">Obtained: </span>{formatDate(l.granted_at)}</p>
                <p className="font-medium" style={{ color: expired ? "#b91c1c" : soon ? AMBER : MUTED }}><span className="md:hidden" style={{ color: MUTED }}>Validity: </span>{validity}</p>
                <div className="flex items-center gap-3 md:justify-end">
                  {l.source === "purchase" && Number(l.price_paid) > 0 && <Link to={receiptLink("licence", l.id)} aria-label="View receipt" className="font-semibold hover:underline" style={{ color: ACCENT }}>Receipt</Link>}
                  {l.sound_available ? (
                    <>
                      <Link to={`/sound/${l.track_id}`} aria-label="View licence" className="font-semibold hover:underline" style={{ color: ACCENT }}>View</Link>
                      {(expired || soon) && <Link to={`/sound/${l.track_id}`} aria-label="Renew licence" className="font-semibold hover:underline" style={{ color: AMBER }}>Renew</Link>}
                    </>
                  ) : <span className="text-xs text-gray-400">Sound unavailable</span>}
                </div>
              </li>
            );
          })}
        </ul>
      </ListPanel>
      {updatingCard && (
        <RenewalCardDialog
          onClose={() => setUpdatingCard(false)}
          onSaved={(done) => {
            setUpdatingCard(false);
            const renewed = (done?.retried || []).filter((r) => r.outcome === "renewed").length;
            toast.success(renewed ? `Card saved. ${renewed === 1 ? "Your licence was renewed." : `${renewed} licences were renewed.`}` : "Card saved. We will use it for your next renewal attempt.");
            list.reload();
          }}
        />
      )}
    </Body>
  );
}

// ---- tab 4: downloads ------------------------------------------------------------------------------------------------------
// Sounds saved for offline listening live in this browser (offlineStore.js), not on the server, so this list is read locally.
// A copy made by someone else on the same browser is not shown.
const ownDownloads = async () => {
  await initOffline();
  const me = storedUserId();
  return (await listOffline()).filter((r) => !r.meta.owner || !me || r.meta.owner === me);
};

function useOwnDownloads() {
  const [state, setState] = useState({ status: "loading", items: [] });
  useEffect(() => {
    let live = true;
    const refresh = () => ownDownloads().then((items) => live && setState({ status: "ready", items })).catch(() => live && setState({ status: "ready", items: [] }));
    refresh();
    const stop = subscribeOffline(refresh);
    return () => { live = false; stop(); };
  }, []);
  return state;
}

function Downloads({ notify }) {
  const { status, items } = useOwnDownloads();
  const [busyId, setBusyId] = useState(null);

  const remove = async (id, title) => {
    if (busyId === id) return;
    setBusyId(id);
    try {
      await removeOffline(id);
      notify(`“${title}” was removed from your downloads.`);
    } finally {
      setBusyId(null);
    }
  };

  if (!isOfflineSupported()) return <Notice>Offline listening isn't supported in this browser, so downloads can't be kept here.</Notice>;
  if (status === "loading") return <ListSkeleton />;
  if (!items.length) return <Notice>You haven't downloaded any sounds yet. Choose Download on a sound page and it will be kept here for offline listening. {findSounds}</Notice>;

  const tracks = items.map((r) => ({
    id: r.id, title: r.meta.title || `Sound #${r.id}`, artist_name: r.meta.artist_name || "", cover_url: r.meta.cover_url || null,
    audio_url: r.meta.audio_url || "offline", duration: r.meta.duration || 0
  }));
  return (
    <ListPanel>
      <ul data-testid="downloads-list">
        {tracks.map((t, i, all) => (
          <SoundRow
            key={t.id} track={t} queue={all}
            detail={[t.artist_name, "Sound", "Available offline"].filter(Boolean).join(" · ")}
            action={<button type="button" onClick={() => remove(t.id, t.title)} disabled={busyId === t.id} className="shrink-0 text-[13px] font-semibold hover:text-red-700 disabled:opacity-60" style={{ color: MUTED }} aria-label={`Remove ${t.title} from downloads`}>Remove</button>}
          />
        ))}
      </ul>
    </ListPanel>
  );
}

export default function Library() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "saved";
  const signedIn = isSignedIn();
  const [counts, setCounts] = useState({ saved: null, playlists: null, licences: null, downloads: null });
  const [toast, notify] = useToast();

  // A message sent along when coming back from a playlist that was just deleted.
  const sentNotice = location.state && location.state.notice;
  useEffect(() => { if (sentNotice) notify(sentNotice); }, [sentNotice]); // eslint-disable-line react-hooks/exhaustive-deps

  // Numbers for the three tabs come in one call; each list then keeps its own number up to date.
  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    discoveryApi.counts().then((c) => { if (live && c) setCounts({ saved: c.saved, playlists: c.playlists, licences: c.licences }); }).catch(() => {});
    return () => { live = false; };
  }, [signedIn]);
  // The Downloads number comes from this browser's own storage and follows saves and removals made on sound pages.
  useEffect(() => {
    if (!signedIn) return undefined;
    let live = true;
    const refresh = () => ownDownloads().then((items) => live && setCounts((c) => ({ ...c, downloads: items.length }))).catch(() => {});
    refresh();
    const stop = subscribeOffline(refresh);
    return () => { live = false; stop(); };
  }, [signedIn]);
  const setTotal = (key) => (value) => setCounts((c) => ({ ...c, [key]: typeof value === "function" ? value(c[key]) : value }));
  const totals = { saved: setTotal("saved"), playlists: setTotal("playlists"), licences: setTotal("licences") };

  return (
    <MusicShell>
      <h1 className="text-[28px] font-bold leading-tight" style={{ color: INK }}>My Library</h1>
      {!signedIn ? (
        <div className="mt-6">
          <Notice action={<Link to="/login" state={{ from: location }} className="mt-3 inline-block rounded-full px-[18px] py-2 text-[13px] font-semibold text-white" style={{ backgroundColor: ACCENT }}>Log in</Link>}>
            Log in to see your saved sounds, playlists, licences and downloads.
          </Notice>
        </div>
      ) : (
        <>
          <div className="mt-4 flex gap-7 overflow-x-auto" role="tablist" aria-label="Library">
            {TABS.map(([key, label]) => (
              <button
                key={key} type="button" role="tab" aria-selected={tab === key} id={`tab-${key}`} aria-controls="library-panel"
                onClick={() => setParams(key === "saved" ? {} : { tab: key }, { replace: true })}
                className="shrink-0 py-1 text-[13px] font-semibold hover:opacity-80"
                style={{ color: tab === key ? ACCENT : MUTED }}
              >
                {label}
                {counts[key] !== null && <span className="ml-1.5 text-xs font-medium" style={{ color: MUTED }} data-testid={`count-${key}`}>{counts[key]}</span>}
              </button>
            ))}
          </div>
          <div className="mt-6" key={tab} role="tabpanel" id="library-panel" aria-labelledby={`tab-${tab}`}>
            {tab === "saved" && <Saved onTotal={totals.saved} notify={notify} />}
            {tab === "playlists" && <Playlists onTotal={totals.playlists} notify={notify} />}
            {tab === "licences" && <Licences onTotal={totals.licences} />}
            {tab === "downloads" && <Downloads notify={notify} />}
          </div>
        </>
      )}
      {toast}
    </MusicShell>
  );
}
